/** Palette JSON import / export — Tier 3 Group D Slice 1b.
 *
 *  The exchange format the picker's manage-palettes dialog uses
 *  for the "Export…" and "Import…" affordances. JSON-only in v1;
 *  Photoshop `.aco` and Illustrator `.ase` binary formats are
 *  deferred (see roadmap "open design questions" — locked
 *  decision: JSON first, binary formats follow).
 *
 *  Versioned wire shape:
 *    {
 *      "fias-editor-palettes": 1,
 *      "palettes": [
 *        { "name": "...", "colors": ["#rrggbb", ...] },
 *        ...
 *      ]
 *    }
 *
 *  Export is one-shot per palette OR a multi-palette bundle. The
 *  importer accepts either shape (single-palette is treated as a
 *  one-element array under the hood). */

import type { UserPalette } from './user-palettes-prefs';
import { normalizeRecentColor } from './recent-colors-prefs';

const SCHEMA_VERSION = 1;
const SCHEMA_KEY = 'fias-editor-palettes';

interface WirePalette {
  name: string;
  colors: string[];
}

interface WireBundle {
  [SCHEMA_KEY]: number;
  palettes: WirePalette[];
}

/** Serialize one or more palettes to the canonical wire format
 *  (pretty-printed for human readability — palettes are small
 *  enough that compactness doesn't matter). */
export function exportPalettesToJson(
  palettes: ReadonlyArray<Pick<UserPalette, 'name' | 'colors'>>,
): string {
  const bundle: WireBundle = {
    [SCHEMA_KEY]: SCHEMA_VERSION,
    palettes: palettes.map((p) => ({ name: p.name, colors: [...p.colors] })),
  };
  return JSON.stringify(bundle, null, 2);
}

export interface ImportResult {
  /** Parsed palettes ready to feed into `createPalette`. */
  palettes: WirePalette[];
  /** Reason the import failed, if any. Empty on success. */
  error?: string;
}

/** Parse a JSON string and return the contained palettes. Returns
 *  an `{ error }` payload (with `palettes: []`) for any malformed
 *  input — callers should surface the error string to the user
 *  rather than silently producing an empty import. */
export function importPalettesFromJson(raw: string): ImportResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return { palettes: [], error: `Invalid JSON: ${(e as Error).message}` };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { palettes: [], error: 'Expected a JSON object at the top level' };
  }
  const obj = parsed as Record<string, unknown>;
  const schemaVersion = obj[SCHEMA_KEY];
  if (typeof schemaVersion !== 'number') {
    return {
      palettes: [],
      error: `Not a Fias palettes file (missing or invalid "${SCHEMA_KEY}" key)`,
    };
  }
  if (schemaVersion !== SCHEMA_VERSION) {
    return {
      palettes: [],
      error: `Unsupported palettes format version ${schemaVersion} (expected ${SCHEMA_VERSION})`,
    };
  }
  if (!Array.isArray(obj.palettes)) {
    return { palettes: [], error: 'Missing or invalid "palettes" array' };
  }
  const valid: WirePalette[] = [];
  for (const entry of obj.palettes) {
    if (typeof entry !== 'object' || entry === null) continue;
    const e = entry as Record<string, unknown>;
    if (typeof e.name !== 'string') continue;
    if (!Array.isArray(e.colors)) continue;
    const colors = e.colors
      .filter((c): c is string => typeof c === 'string')
      .map((c) => normalizeRecentColor(c))
      .filter((c): c is string => c !== null);
    if (colors.length === 0) continue;
    valid.push({ name: e.name.trim() || 'Imported palette', colors });
  }
  if (valid.length === 0) {
    return { palettes: [], error: 'No valid palettes found in file' };
  }
  return { palettes: valid };
}
